"use client";

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { getCurrentLanguageForAPI } from '@repo/i18next-config';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

export function useApiWithLanguage() {
  const router = useRouter();

  const fetchWithLanguage = useCallback(async (url: string, options: FetchOptions = {}) => {
    const acceptLanguage = getCurrentLanguageForAPI();

    const headers = new Headers(options.headers);
    // Use a custom header to avoid browser's accept-language interfering
    headers.set('X-App-Language', acceptLanguage);
    // Also set Accept-Language for good measure
    headers.set('Accept-Language', acceptLanguage);

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 errors by redirecting to login
    // Skip logout/redirect for session endpoint as it's expected to return 401 when not authenticated
    if (response.status === 401 && !url.includes('/api/auth/session')) {
      // Call logout route to clear all cookies (using direct fetch to avoid recursion)
      await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {
        // Ignore logout errors, still redirect to login
      });

      const data = await response.json().catch(() => ({}));
      const redirectPath = data.redirect || '/login';
      router.push(`${redirectPath}?session_expired=true`);
      throw new Error('Unauthorized - redirecting to login');
    }

    return response;
  }, [router]);

  return { fetchWithLanguage };
}